export const USER_NOTIFICATION_TYPE = {
  MaintenanceWindow: 1,
  GeneralNotification: 2,
} as const

export const USER_NOTIFICATION_DISPLAY_MODE = {
  FixedBanner: 1,
  Popup: 2,
} as const

export const USER_NOTIFICATION_AUDIENCE = {
  AllUsers: 1,
  SpecificUsers: 2,
} as const

export const USER_NOTIFICATION_ACTION = {
  NotificationSent: 1,
  NotificationReceived: 2,
  BannerDisplayed: 3,
  PopupDisplayed: 4,
  PopupClosed: 5,
  BannerClosed: 6,
  NotificationAutoClosed: 7,
  LogoutCountdownStarted: 8,
  UserAutoLoggedOut: 9,
  NotificationCancelled: 10,
  NotificationCreated: 11,
  NotificationUpdated: 12,
  NotificationStarted: 13,
  NotificationEnded: 14,
  PushNotificationSent: 15,
  PushNotificationDelivered: 16,
  UserForceLoggedOut: 17,
} as const

export type ActiveUserNotification = {
  notificationId: number
  organizationId: number
  notificationTypeId: number
  notificationType?: string
  title: string
  messageHtml: string
  displayModeId: number
  displayMode?: string
  startDateTimeUtc: string
  endDateTimeUtc?: string | null
  logoutDeadlineUtc?: string | null
  remainingLogoutSeconds?: number | null
  popupDisplaySeconds?: number | null
  priority: number
  status?: string
  audienceTypeId?: number
  inAppEnabled?: boolean
  popupEnabled?: boolean
  pushEnabled?: boolean
  forceLogoutEnabled?: boolean
  forceLogoutSeconds?: number | null
}

export const getNotificationSessionId = () => {
  const key = 'ssNotificationSessionId'
  let id = sessionStorage.getItem(key)
  if (!id) {
    id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem(key, id)
  }
  return id
}

export const formatCountdown = (totalSeconds: number) => {
  const s = Math.max(0, Math.floor(totalSeconds))
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

export const toLocalInputValue = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`
}

export const fromLocalInputToUtcIso = (local: string) => {
  if (!local) return ''
  return new Date(local).toISOString()
}

const pad2 = (n: number) => String(n).padStart(2, '0')

export const formatLocalDateTimeInput = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(
    d.getMinutes()
  )}`

/** Round up to the next 5-minute boundary (keeps exact boundary as-is). */
export const ceilToNext5Minutes = (d: Date) => {
  const ms = 5 * 60 * 1000
  const t = d.getTime()
  const rounded = Math.ceil(t / ms) * ms
  return new Date(rounded)
}

/** Minimum allowed start: now rounded up to next 5-minute slot (at least 5 minutes ahead if already on a slot). */
export const getMinimumStartLocal = () => {
  const min = ceilToNext5Minutes(new Date(Date.now() + 5 * 60 * 1000))
  return formatLocalDateTimeInput(min)
}

/**
 * Current + 2 Hours: start = ceil5(now + 2h), end = start + 30 minutes (5-min aligned).
 */
export const applyDefaultScheduleShortcut = () => {
  const start = ceilToNext5Minutes(new Date(Date.now() + 2 * 60 * 60 * 1000))
  const end = new Date(start.getTime() + 30 * 60 * 1000)
  return { startLocal: formatLocalDateTimeInput(start), endLocal: formatLocalDateTimeInput(end) }
}

/**
 * Current + 15 Minutes: e.g. 6:18 → +15 = 6:33 → ceil5 = 6:35, end = 6:40.
 */
export const applyPlus15ScheduleShortcut = () => {
  const start = ceilToNext5Minutes(new Date(Date.now() + 15 * 60 * 1000))
  const end = new Date(start.getTime() + 5 * 60 * 1000)
  return { startLocal: formatLocalDateTimeInput(start), endLocal: formatLocalDateTimeInput(end) }
}

export const isOnFiveMinuteBoundary = (local: string) => {
  if (!local) return false
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return false
  return d.getMinutes() % 5 === 0 && d.getSeconds() === 0
}

export const validateNotificationScheduleLocal = (startLocal: string, endLocal: string) => {
  if (!startLocal || !endLocal) return 'Start and end are required.'
  const start = new Date(startLocal)
  const end = new Date(endLocal)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Invalid date/time.'
  if (start.getMinutes() % 5 !== 0) return 'Start time must use a 5-minute interval (…:00, :05, :10, …).'
  if (end.getMinutes() % 5 !== 0) return 'End time must use a 5-minute interval (…:00, :05, :10, …).'
  const minStart = ceilToNext5Minutes(new Date(Date.now() + 5 * 60 * 1000))
  if (start < minStart) {
    return `Start must be at least the next 5-minute slot (${formatLocalDateTimeInput(minStart)} local).`
  }
  if (end <= start) return 'End time must be greater than start time.'
  return null
}
